import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { refreshToken, getUserRBAC } from '../store/authSlice';
import { tokenRefreshManager } from '../utils/tokenRefreshManager';

/**
 * Hook to manage automatic JWT token refresh
 * Should be used in the main app component or auth provider
 */
export const useTokenRefresh = () => {
    const dispatch = useAppDispatch();
    const { token, isAuthenticated, user } = useAppSelector((state) => state.auth);

    // Callback to handle automatic refresh
    const handleAutomaticRefresh = useCallback(async () => {
        try {
            await dispatch(refreshToken())
                .unwrap()
                .then(async () => {
                    await dispatch(getUserRBAC());
                });
        } catch (error) {
            console.error('Automatic token refresh failed:', error);
            // The reducer will handle logout on failure
        }
    }, [dispatch]);

    // Start refresh scheduler when user is authenticated
    useEffect(() => {
        if (token && isAuthenticated && user) {
            tokenRefreshManager.startRefreshScheduler(token, {
                refreshThresholdMinutes: 5, // Refresh 5 minutes before expiry
                onRefresh: handleAutomaticRefresh,
                onError: (error) => {
                    console.error('Token refresh error:', error);
                },
            });
        } else {
            // Stop scheduler if user is not authenticated
            tokenRefreshManager.stopRefreshScheduler();
        }

        // Cleanup on unmount
        return () => {
            tokenRefreshManager.stopRefreshScheduler();
        };
    }, [token, isAuthenticated, user, handleAutomaticRefresh]);

    // Return utility functions for manual control
    return {
        isRefreshing: tokenRefreshManager.isCurrentlyRefreshing,
        manualRefresh: useCallback(async () => {
            await tokenRefreshManager.manualRefresh();
        }, []),
        stopRefresh: useCallback(() => {
            tokenRefreshManager.stopRefreshScheduler();
        }, []),
    };
};
